// BulkPersonnelUpload.tsx — Bulk pass-holder import via Excel + ZIP
//
// Mirrors the legacy flow: download an Excel template, fill 1–25 pass holders, zip the
// supporting documents named {ID}_PHOTO.JPG / {ID}_QID.PDF / {ID}_PASSPORT.PDF /
// {ID}_VISA.PDF / {ID}_OTHERS.PDF, then upload the Excel + ZIP together.
//
// Talks to the backend bulk endpoints:
//   GET  /personnel/bulk-template   → template download
//   POST /personnel/bulk-upload     → mode=preview (validate) then mode=commit (create)
//
// On commit, the created Personnel records are returned to the parent so they can be
// auto-selected into the gate pass being built.
import React, { useState, useCallback } from 'react';
import './BulkPersonnelUpload.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface RowResult {
  rowNumber: number;
  data: {
    firstName?: string;
    lastName?: string;
    nationalIdNo?: string;
    nationality?: string;
    visaExpiry?: string;
    [key: string]: any;
  };
  documents: string[];
  warnings: string[];
  errors: string[];
  status: 'valid' | 'invalid' | 'duplicate';
}

interface PreviewResponse {
  success: boolean;
  mode: string;
  summary: { total: number; valid: number; invalid: number; duplicate: number; created?: number; reused?: number; failed?: number };
  results: RowResult[];
  created?: any[];
  reused?: any[];
  selected?: any[];
  failed?: { rowNumber: number; error: string }[];
  error?: string;
}

interface BulkPersonnelUploadProps {
  getAccessToken: () => Promise<string>;
  onClose: () => void;
  /** Called after a successful commit with the personnel to add (created + reused). */
  onComplete: (createdPersonnel: any[]) => void;
  onStatusUpdate?: (status: { type: 'success' | 'error'; message: string }) => void;
  /**
   * 'request'    — launched from the gate pass form: duplicates are selectable and added
   *                to the request (existing records reused, not re-imported).
   * 'management' — launched from Personnel Management: only non-duplicates are selectable;
   *                this is a pure import into the company pool.
   */
  usage?: 'request' | 'management';
}

type Stage = 'select' | 'preview' | 'result';

export const BulkPersonnelUpload: React.FC<BulkPersonnelUploadProps> = ({
  getAccessToken,
  onClose,
  onComplete,
  onStatusUpdate,
  usage = 'request'
}) => {
  const isRequest = usage === 'request';
  // Request flow can select duplicates (to reuse existing records); management cannot.
  const isSelectable = useCallback(
    (status: RowResult['status']) => (isRequest ? status !== 'invalid' : status === 'valid'),
    [isRequest]
  );
  const [stage, setStage] = useState<Stage>('select');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  // Row numbers the user has ticked for import (only valid rows are selectable).
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Canonical document display order — OTHERS always last.
  const DOC_ORDER = ['PHOTO', 'PASSPORT', 'QID', 'VISA', 'OTHERS'];
  const orderDocs = (docs: string[]) =>
    [...docs].sort((a, b) => {
      const ia = DOC_ORDER.indexOf(a); const ib = DOC_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  // ── Template download ──────────────────────────────────────────────
  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE_URL}/personnel/bulk-template`, { method: 'GET' });
      if (!resp.ok) throw new Error(`Template download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PassHolders_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || 'Could not download the template.');
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  // ── Upload helper (preview / commit) ───────────────────────────────
  const submit = useCallback(async (mode: 'preview' | 'commit', selection?: number[]): Promise<PreviewResponse | null> => {
    if (!zipFile) {
      setError('Please attach the ZIP file (it must contain the filled Excel and the documents).');
      return null;
    }
    setIsBusy(true);
    setBusyLabel(mode === 'preview' ? 'Validating…' : 'Importing pass holders…');
    setError(null);
    try {
      const token = await getAccessToken();
      const form = new FormData();
      form.append('zip', zipFile);
      form.append('mode', mode);
      if (mode === 'commit' && selection) form.append('selectedRows', JSON.stringify(selection));
      const resp = await fetch(`${API_BASE_URL}/personnel/bulk-upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form
      });
      const json: PreviewResponse = await resp.json();
      if (!resp.ok || !json.success) {
        throw new Error(json?.error || `Request failed (${resp.status})`);
      }
      return json;
    } catch (e: any) {
      setError(e.message || 'Upload failed. Please try again.');
      return null;
    } finally {
      setIsBusy(false);
      setBusyLabel('');
    }
  }, [zipFile, getAccessToken]);

  const handleValidate = useCallback(async () => {
    const res = await submit('preview');
    if (res) {
      setPreview(res);
      // Pre-select every selectable row by default.
      setSelectedRows(new Set(res.results.filter(r => isSelectable(r.status)).map(r => r.rowNumber)));
      setStage('preview');
    }
  }, [submit, isSelectable]);

  const handleCommit = useCallback(async () => {
    const res = await submit('commit', Array.from(selectedRows));
    if (res) {
      setResult(res);
      setStage('result');
      onStatusUpdate?.({
        type: 'success',
        message: isRequest
          ? `${(res.summary.created || 0) + (res.summary.reused || 0)} pass holder(s) added to the request.`
          : `${res.summary.created || 0} pass holder(s) imported.`
      });
    }
  }, [submit, onStatusUpdate, selectedRows, isRequest]);

  const selectableRowNumbers = (preview?.results || []).filter(r => isSelectable(r.status)).map(r => r.rowNumber);
  const allSelected = selectableRowNumbers.length > 0 && selectableRowNumbers.every(n => selectedRows.has(n));

  const toggleRow = (rowNumber: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowNumber)) next.delete(rowNumber); else next.add(rowNumber);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedRows(allSelected ? new Set() : new Set(selectableRowNumbers));
  };

  const handleDone = useCallback(() => {
    // Add both newly-created and existing-duplicate personnel to the gate pass selection.
    onComplete(result?.selected || result?.created || []);
  }, [onComplete, result]);

  // ── Render helpers ─────────────────────────────────────────────────
  // Minimal palette: gray by default, orange accent for valid, muted red for invalid.
  const statusChip = (s: RowResult['status']) => (
    <span className={`bulk-status bulk-status-${s}`}>
      {s === 'valid' ? 'Valid' : s === 'duplicate' ? 'Duplicate' : 'Invalid'}
    </span>
  );

  const rowName = (r: RowResult) => `${r.data.firstName || ''} ${r.data.lastName || ''}`.trim() || '(no name)';

  return (
    <div className="bulk-upload-modal">
      {/* Header */}
      <div className="bulk-upload-header">
        <div className="bulk-upload-title">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <h3>Bulk Upload Pass Holders</h3>
        </div>
        <button onClick={onClose} className="bulk-close-btn" title="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Stepper */}
      <div className="bulk-stepper">
        <span className={stage === 'select' ? 'active' : 'done'}>1. Prepare &amp; Upload</span>
        <span className={stage === 'preview' ? 'active' : stage === 'result' ? 'done' : ''}>2. Review</span>
        <span className={stage === 'result' ? 'active' : ''}>3. Done</span>
      </div>

      <div className="bulk-upload-body">
        {error && <div className="bulk-error-banner">{error}</div>}

        {/* ── Stage 1: select ─────────────────────────────────────── */}
        {stage === 'select' && (
          <>
            <div className="bulk-instructions">
              <ol>
                <li><strong>Download the template</strong> and fill in the pass-holder details (1–25 rows). Columns marked <strong>*</strong> are mandatory.</li>
                <li>Name each document <code>{'{ID}_TYPE.ext'}</code> where <code>{'{ID}'}</code> is the Qatar/National ID (or Passport No). e.g. <code>28412345678_QID.PDF</code>, <code>28412345678_PHOTO.JPG</code>. Photos must be <strong>JPG</strong>; other documents <strong>PDF</strong>.</li>
                <li>Put the <strong>filled Excel</strong> and all documents in one folder and <strong>compress it to a single .zip</strong>.</li>
                <li>Upload that one ZIP below, then validate.</li>
              </ol>
              <button className="btn btn-secondary bulk-template-btn" onClick={handleDownloadTemplate} disabled={downloadingTemplate}>
                {downloadingTemplate ? 'Preparing…' : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download Template
                  </>
                )}
              </button>
            </div>

            <label className={`bulk-file-picker bulk-file-picker-single ${zipFile ? 'has-file' : 'needs-file'}`}>
              <span className="bulk-file-label">ZIP file (.zip) — filled Excel + documents *</span>
              <input type="file" accept=".zip" onChange={(e) => setZipFile(e.target.files?.[0] || null)} />
              <span className="bulk-file-name">{zipFile ? zipFile.name : 'No file selected'}</span>
            </label>
          </>
        )}

        {/* ── Stage 2: preview ────────────────────────────────────── */}
        {stage === 'preview' && preview && (
          <>
            <div className="bulk-summary">
              <span className="bulk-summary-pill total">{preview.summary.total} rows</span>
              <span className={`bulk-summary-pill ${preview.summary.valid ? 'valid' : ''}`}>{preview.summary.valid} valid</span>
              {preview.summary.duplicate > 0 && <span className="bulk-summary-pill">{preview.summary.duplicate} duplicate</span>}
              {preview.summary.invalid > 0 && <span className="bulk-summary-pill invalid">{preview.summary.invalid} invalid</span>}
              <span className="bulk-summary-selected">{selectedRows.size} selected for import</span>
            </div>
            <div className="bulk-table-wrap">
              <table className="bulk-table">
                <thead>
                  <tr>
                    <th className="bulk-check-col">
                      <input
                        type="checkbox"
                        aria-label="Select all valid rows"
                        checked={allSelected}
                        disabled={selectableRowNumbers.length === 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th>#</th>
                    <th>Name</th>
                    <th>Passport</th>
                    <th>QID</th>
                    <th>Nationality</th>
                    <th>Docs</th>
                    <th>Status</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.results.map((r) => {
                    const selectable = isSelectable(r.status);
                    const checked = selectedRows.has(r.rowNumber);
                    return (
                      <tr
                        key={r.rowNumber}
                        className={`bulk-row ${r.status} ${checked ? 'selected' : ''}`}
                        onClick={() => selectable && toggleRow(r.rowNumber)}
                        style={{ cursor: selectable ? 'pointer' : 'default' }}
                      >
                        <td className="bulk-check-col" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            aria-label={`Select row ${r.rowNumber}`}
                            checked={checked}
                            disabled={!selectable}
                            onChange={() => toggleRow(r.rowNumber)}
                          />
                        </td>
                        <td>{r.rowNumber}</td>
                        <td>{rowName(r)}</td>
                        <td>{r.data.passportNumber || '—'}</td>
                        <td>{r.data.nationalIdNo || '—'}</td>
                        <td>{r.data.nationality || '—'}</td>
                        <td>{r.documents.length ? orderDocs(r.documents).join(', ') : '—'}</td>
                        <td>{statusChip(r.status)}</td>
                        <td className="bulk-notes">
                          {r.errors.map((e, i) => <div key={`e${i}`} className="bulk-note-error">{e}</div>)}
                          {r.warnings.map((w, i) => <div key={`w${i}`} className="bulk-note-warn">{w}</div>)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Stage 3: result ─────────────────────────────────────── */}
        {stage === 'result' && result && (
          <div className="bulk-result">
            <div className="bulk-result-icon">
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#F3694A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h4>
              {isRequest
                ? `${(result.summary.created || 0) + (result.summary.reused || 0)} pass holder(s) added`
                : `${result.summary.created || 0} pass holder(s) imported`}
            </h4>
            <p>
              {result.summary.created || 0} newly imported
              {isRequest && result.summary.reused ? `, ${result.summary.reused} already existed (added to request)` : ''}
              {!isRequest && result.summary.duplicate ? `, ${result.summary.duplicate} skipped (already exist)` : ''}
              {result.summary.invalid ? `, ${result.summary.invalid} skipped (invalid)` : ''}
              {result.summary.failed ? `, ${result.summary.failed} failed to save` : ''}.
            </p>
            {!!(result.failed && result.failed.length) && (
              <ul className="bulk-failed-list">
                {result.failed.map((f) => <li key={f.rowNumber}>Row {f.rowNumber}: {f.error}</li>)}
              </ul>
            )}
            <p className="bulk-result-hint">
              {isRequest
                ? 'New pass holders were added to your company personnel; existing ones were reused. All selected pass holders have been added to this gate pass.'
                : 'The imported pass holders have been added to your company personnel.'}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bulk-upload-footer">
        {isBusy && <span className="bulk-busy">{busyLabel}</span>}
        {stage === 'select' && (
          <>
            <button className="btn btn-secondary" onClick={onClose} disabled={isBusy}>Cancel</button>
            <button className="btn btn-primary" onClick={handleValidate} disabled={isBusy || !zipFile}>
              {isBusy ? 'Validating…' : 'Validate'}
            </button>
          </>
        )}
        {stage === 'preview' && (
          <>
            <button className="btn btn-secondary" onClick={() => setStage('select')} disabled={isBusy}>Back</button>
            <button
              className="btn btn-primary"
              onClick={handleCommit}
              disabled={isBusy || selectedRows.size === 0}
            >
              {isBusy ? 'Importing…' : `Import ${selectedRows.size} selected`}
            </button>
          </>
        )}
        {stage === 'result' && (
          <button className="btn btn-primary" onClick={handleDone}>Done</button>
        )}
      </div>
    </div>
  );
};

export default BulkPersonnelUpload;
